import { atom, WritableAtom } from "jotai";
import { Tool } from "ai";
import { ToolComponentProps } from "@zapier/ai-toolkit/client";

import { choicesApiAtom } from "./tools/choices_api";
import { getZapAtom } from "./tools/get_zap";
import { getTableByIdAtom } from "./tools/get_table_by_id";
import { authenticationCheckAtom } from "./tools/authentication_check";
import { needsApiAtom } from "./tools/needs_api";
import { updateZapAtom } from "./tools/update_zap";
import { validateZslAtom } from "./tools/validate_zsl";
import { stepTestingAtom } from "./tools/step_testing";
import { edit_canvas } from "./tools/edit_canvas";
import { network_request } from "./tools/network_request";
import { search_tool } from "./tools/search_tool";
import { search_templates_tool } from "./tools/search_templates_tool";
import { search_recipes_tool } from "./tools/search_recipes_tool";
import { show_build_mode_suggestions } from "./tools/show_build_mode_suggestions";
import {
  nodeDescription,
  renderAnnotationNodesSetter,
  tourToolAtom,
} from "./tools/tour";

export type JotaiTool = Omit<Tool, "execute"> & {
  shouldInclude?: () => boolean;
  execute: WritableAtom<null, [any], any>;
  component?: React.FC<ToolComponentProps<any, any>>;
};

export {
  edit_canvas,
  network_request,
  search_recipes_tool,
  search_templates_tool,
  search_tool,
  show_build_mode_suggestions,
};

// Shared jotai atoms for modifying the canvas state
export { nodeDescription, renderAnnotationNodesSetter };

export const toolsAtom = atom<Record<string, JotaiTool>>((get) => {
  const choicesApi = get(choicesApiAtom);
  const getZap = get(getZapAtom);
  const getTableById = get(getTableByIdAtom);
  const authenticationCheck = get(authenticationCheckAtom);
  const needsApi = get(needsApiAtom);
  const updateZap = get(updateZapAtom);
  const validateZsl = get(validateZslAtom);
  const testZapStep = get(stepTestingAtom);
  const tourTool = get(tourToolAtom);

  const allTools = {
    ...choicesApi,
    ...getZap,
    ...getTableById,
    ...authenticationCheck,
    ...needsApi,
    ...updateZap,
    ...validateZsl,
    ...testZapStep,
    ...tourTool,
  };

  const filteredTools = Object.fromEntries(
    Object.entries(allTools).filter(([, tool]) => {
      let shouldIncludeTool = true;
      if ("shouldInclude" in tool && tool.shouldInclude) {
        shouldIncludeTool = tool.shouldInclude();
      }
      return shouldIncludeTool;
    }),
  );

  return filteredTools;
});

toolsAtom.debugLabel = "toolsAtom";
